const updateCartBubbles = (count) => {
  document.querySelectorAll('.cart-count-bubble span[aria-hidden="true"]').forEach((span) => {
    span.textContent = count;
  });
};

class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0, event);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  getQuantityInput(line) {
    return (
      this.querySelector(`#Quantity-${line}`) ||
      this.querySelector(`#Drawer-quantity-${line}`) ||
      this.querySelector(`#CartItem-quantity-${line}`) ||
      this.querySelector(`#CartDrawer-quantity-${line}`) ||
      this.querySelector(`.quantity__input[data-index="${line}"]`)
    );
  }

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      return this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  dispatchCartUpdatedEvent(cartData) {
    const event = new CustomEvent('cart:updated', {
      detail: {
        cart: cartData,
        totalPrice: cartData.total_price,
        items: cartData.items,
        timestamp: Date.now()
      },
      bubbles: true
    });
    document.dispatchEvent(event);
  }

  getLineItemElement(line) {
    return document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
  }

  getFocusRestoreTarget(line, name, event) {
    const lineItem = this.getLineItemElement(line);
    const activeElement = document.activeElement;
    const eventTarget = event?.target;

    const resolveSelector = (element) => {
      if (!(element instanceof Element)) return null;
      if (lineItem && !lineItem.contains(element)) return null;

      if (element.matches('.quantity__button[name]')) {
        return `.quantity__button[name="${element.getAttribute('name')}"]`;
      }

      if (element.matches('.quantity__input')) {
        return '.quantity__input';
      }

      if (element.id) {
        return `#${CSS.escape(element.id)}`;
      }

      return null;
    };

    return resolveSelector(activeElement) || resolveSelector(eventTarget) || (name ? `[name="${name}"]` : null);
  }

  restoreFocusAfterUpdate(line, selector, cartDrawerWrapper, parsedState) {
    const restore = () => {
      const lineItem = this.getLineItemElement(line);
      const target = selector && lineItem ? lineItem.querySelector(selector) : null;

      if (target) {
        if (cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper, target);
        } else {
          target.focus();
        }
        return;
      }

      if (parsedState.item_count === 0 && cartDrawerWrapper) {
        trapFocus(cartDrawerWrapper, cartDrawerWrapper.querySelector('a') || cartDrawerWrapper.querySelector('.drawer__inner'));
        return;
      }

      const fallbackTarget = document.querySelector('.cart-item__name');
      if (fallbackTarget && cartDrawerWrapper) {
        trapFocus(cartDrawerWrapper, fallbackTarget);
      }
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(restore);
    });
  }

  resetQuantityInput(id) {
    const input = this.getQuantityInput(id);
    if (!input) return;
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = '';

    if (inputValue === 0) {
      this.updateQuantity(
        index,
        inputValue,
        event
      );
      return;
    }

    if (inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min);
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace('[max]', event.target.max);
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace('[step]', event.target.step);
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        event,
        document.activeElement.getAttribute('name'),
        event.target.dataset.quantityVariantId
      );
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  handleCartError(line, errorMessage, variantId, focusRestoreTarget = null) {
    const sections = this.getSectionsToRender().map((section) => section.section);
    const body = JSON.stringify({
      sections: sections.join(','),
      sections_url: window.location.pathname
    });

    fetch(`${routes.cart_update_url}`, { ...fetchConfig(), body })
      .then((response) => response.text())
      .then((state) => {
        const freshState = JSON.parse(state);

        this.getSectionsToRender().forEach((section) => {
          const sectionElement = document.getElementById(section.id);
          if (!sectionElement) return;

          const elementToReplace = sectionElement.querySelector(section.selector) || sectionElement;
          if (elementToReplace && freshState.sections && freshState.sections[section.section || section.id]) {
            elementToReplace.innerHTML = this.getSectionInnerHTML(
              freshState.sections[section.section || section.id],
              section.selector
            );
          }
        });

        const cartDrawerWrapper = document.querySelector('cart-drawer');
        if (cartDrawerWrapper) {
          cartDrawerWrapper.classList.toggle('is-empty', freshState.item_count === 0);
        }

        updateCartBubbles(freshState.item_count);

        this.updateLiveRegions(line, errorMessage);
        this.restoreFocusAfterUpdate(line, focusRestoreTarget, cartDrawerWrapper, freshState);
        this.dispatchCartUpdatedEvent(freshState);
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: freshState, variantId: variantId });
      }).catch((e) => {
        console.error(e);
      });
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      return fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
          return fetch(`${routes.cart_url}.js`);
        })
        .then((response) => response.json())
        .then((cart) => {
          updateCartBubbles(cart.item_count);
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      return fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          if (sourceQty) {
            this.classList.toggle('is-empty', sourceQty.classList.contains('is-empty'));
            this.innerHTML = sourceQty.innerHTML;
          }
          return fetch(`${routes.cart_url}.js`);
        })
        .then((response) => response.json())
        .then((cart) => {
          updateCartBubbles(cart.item_count);
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }


  getSectionsToRender() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      return [
        {
          id: 'cart-drawer',
          section: 'cart-drawer',
          selector: '.drawer__inner',
        },
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '#cart-icon-bubble',
        }
      ];
    }

    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '#main-cart-items .js-contents',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '#main-cart-footer .js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-icon-bubble-page',
        section: 'cart-icon-bubble-count',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      }
    ];
  }

  updateQuantity(line, quantity, event, name, variantId) {
    const focusRestoreTarget = this.getFocusRestoreTarget(line, name, event);
    this.enableLoading(line);
    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });
    const eventTarget = event.currentTarget instanceof CartRemoveButton ? 'clear' : 'change';

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);

        if (parsedState.errors) {
          this.handleCartError(line, parsedState.errors, variantId, focusRestoreTarget);
          return;
        }

        CartPerformance.measure(`${eventTarget}:paint-updated-sections"`, () => {
          const quantityElement = this.getQuantityInput(line);
          const items = document.querySelectorAll('.cart-item');
          const cartDrawerWrapper = document.querySelector('cart-drawer');
          const cartFooter = document.getElementById('main-cart-footer');

          if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
          if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);
          this.classList.toggle('is-empty', parsedState.item_count === 0);

          this.getSectionsToRender().forEach((section) => {
            const sectionElement = document.getElementById(section.id);
            if (!sectionElement) return;
            const elementToReplace = sectionElement.querySelector(section.selector) || sectionElement;
            elementToReplace.innerHTML = this.getSectionInnerHTML(
              parsedState.sections[section.section],
              section.selector
            );
          });

          updateCartBubbles(parsedState.item_count);

          const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
          let message = '';
          if (quantityElement && items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
            if (typeof updatedValue === 'undefined') {
              message = window.cartStrings.error;
            } else {
              message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
            }
          }
          this.updateLiveRegions(line, message);
          this.restoreFocusAfterUpdate(line, focusRestoreTarget, cartDrawerWrapper, parsedState);

          this.dispatchCartUpdatedEvent(parsedState);
        });

        CartPerformance.measureFromEvent(`${eventTarget}:user-action`, event);

        updateCartBubbles(parsedState.item_count);
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
      })
      .catch(() => {
        this.querySelectorAll('.loading-spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError = document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`) || document.getElementById(`CartItem-LineItemError-${line}`);
    if (lineItemError && lineItemError.querySelector('.cart-item__error-text')) {
      lineItemError.querySelector('.cart-item__error-text').textContent = message;
    }

    if (this.lineItemStatusElement) {
      this.lineItemStatusElement.setAttribute('aria-hidden', true);
    }

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');

    if (cartStatus) {
      cartStatus.setAttribute('aria-hidden', false);
      setTimeout(() => {
        if (cartStatus) {
          cartStatus.setAttribute('aria-hidden', true);
        }
      }, 1000);
    }

    const slider = document.querySelector('.cart-drawer__collections slider-component');
    if (slider) slider.reInit();
  }

  getSectionInnerHTML(html, selector) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const target = doc.querySelector(selector);
    return target ? target.innerHTML : '';
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading-spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading-spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems && mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading-spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading-spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
              .then(() => CartPerformance.measureFromEvent('note-update:user-action', event));
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}
